import { Controller } from "@hotwired/stimulus"

// How much the open list overlaps the cell it belongs to
const OVERLAP = 24

const lists = new Set()

// Connects to data-controller="kind-select"
export default class extends Controller {
  static targets = ["display", "select", "list", "option"]

  connect() {
    this.boundHide = this.hide.bind(this)
    this.boundClose = () => this.close()

    lists.add(this)
    document.addEventListener("click", this.boundHide)
    window.addEventListener("scroll", this.boundClose, true)
    window.addEventListener("resize", this.boundClose)
  }

  disconnect() {
    lists.delete(this)
    document.removeEventListener("click", this.boundHide)
    window.removeEventListener("scroll", this.boundClose, true)
    window.removeEventListener("resize", this.boundClose)
  }

  open(event) {
    event.stopPropagation()

    if (this.isOpen) {
      this.close()
      return
    }

    lists.forEach(list => { if (list !== this) list.close() })

    this.listTarget.classList.remove("hidden")
    this.raiseCell()
    this.position()
    this.currentOption().focus()
  }

  select(event) {
    event.preventDefault()
    event.stopPropagation()

    const option = event.currentTarget
    this.close()
    this.focusCell()

    if (option.dataset.kind === this.selectTarget.value) return

    this.selectTarget.value = option.dataset.kind
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))

    // The editable-cell controller resets the display to the option's plain text
    // while saving, so put the badge back once it is done.
    this.displayTarget.innerHTML = option.firstElementChild.outerHTML
  }

  navigate(event) {
    if (!["ArrowUp", "ArrowDown", "Escape", "Tab", "Enter", " "].includes(event.key)) return

    // Keep the grid navigation of the surrounding cell out of the open list
    event.stopPropagation()

    // The option is a button, it activates itself
    if (event.key === "Enter" || event.key === " ") return

    event.preventDefault()

    if (event.key === "Escape" || event.key === "Tab") {
      this.close()
      this.focusCell()
      return
    }

    const options = this.optionTargets
    const step = event.key === "ArrowDown" ? 1 : -1
    const index = options.indexOf(document.activeElement)

    options[(index + step + options.length) % options.length].focus()
  }

  hide(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  close() {
    this.listTarget.classList.add("hidden")
    this.restoreCell()
  }

  // A pinned cell keeps the open list inside its own stacking context, so lift
  // the cell above the other rows while the list is open
  raiseCell() {
    this.cellZIndex ??= this.element.style.zIndex
    this.element.style.zIndex = "60"
  }

  restoreCell() {
    if (this.cellZIndex === undefined) return

    this.element.style.zIndex = this.cellZIndex
    this.cellZIndex = undefined
  }

  get isOpen() {
    return !this.listTarget.classList.contains("hidden")
  }

  position() {
    const cell = this.element.getBoundingClientRect()
    const height = this.listTarget.offsetHeight
    const bottom = this.element.closest(".overflow-auto")?.getBoundingClientRect().bottom ?? window.innerHeight
    const fitsBelow = bottom - (cell.bottom - OVERLAP) > height

    this.listTarget.style.left = `${cell.left}px`
    this.listTarget.style.top = fitsBelow ? `${cell.bottom - OVERLAP}px` : `${cell.top - height + OVERLAP}px`
  }

  currentOption() {
    return this.optionTargets.find(option => option.dataset.kind === this.selectTarget.value) || this.optionTargets[0]
  }

  focusCell() {
    this.element.setAttribute("tabindex", "0")
    this.element.focus()
  }
}
